/**
 * wd-scroll-carousel-loop.js  (v2)
 * ---------------------------------------------------------------------------
 * Seamless infinite loop for the theme's <scroll-carousel>.
 *
 * Opt-in: add a `wd-loop` attribute to the <scroll-carousel> you want to loop.
 *
 * v2 fixes:
 *   - Clones no longer boot Shopify custom elements (product-card / product-form
 *     / buy-buttons / variant-picker ...). Those tags are downgraded to plain
 *     <div> BEFORE insertion, so cloning can't crash theme.js.
 *   - The prev/next buttons are kept enabled (a loop has no edges), so the theme
 *     can't grey them out anymore.
 * ---------------------------------------------------------------------------
 */
(() => {
  const isRTL = () => document.dir === 'rtl';

  class WdInfiniteLoop {
    constructor(carousel) {
      this.carousel = carousel;
      this.originals = [];
      this.setWidth = 0;
      this.leadWidth = 0;
      this.locked = false;
      this.dragging = false;
      this.ready = false;
      requestAnimationFrame(() => this.build());
    }

    gap() {
      const cs = getComputedStyle(this.carousel);
      return parseFloat(cs.columnGap || cs.gap || '0') || 0;
    }

    // Span of the real cards relative to each other = one full set, valid even
    // after clones are added.
    measureSetWidth() {
      const first = this.originals[0];
      const last = this.originals[this.originals.length - 1];
      return last.offsetLeft + last.offsetWidth - first.offsetLeft + this.gap();
    }

    // Replace a custom element with a plain <div>, keeping its attributes and
    // children. Prevents the clone from ever upgrading / running theme JS.
    downgrade(el) {
      const div = document.createElement('div');
      for (const attr of Array.from(el.attributes)) {
        if (attr.name === 'is') continue;
        div.setAttribute(attr.name, attr.value);
      }
      while (el.firstChild) div.appendChild(el.firstChild);
      el.replaceWith(div);
      return div;
    }

    makeClone(original) {
      let clone = original.cloneNode(true); // detached: nothing upgrades yet

      // Downgrade every custom-element descendant (tag names contain a dash).
      clone.querySelectorAll('*').forEach((el) => {
        el.removeAttribute('is');
        if (el.localName.includes('-')) this.downgrade(el);
      });
      // Downgrade the root too if it is a custom element.
      if (clone.localName.includes('-')) {
        clone = this.downgrade(clone);
      } else {
        clone.removeAttribute('is');
      }

      // Kill any remaining form controls so clones never touch cart/variants.
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('[for]').forEach((el) => el.removeAttribute('for'));
      clone.querySelectorAll('input, select, textarea, button').forEach((el) => {
        el.removeAttribute('name');
        el.removeAttribute('id');
        try { el.disabled = true; } catch (e) {}
      });

      clone.setAttribute('data-wd-loop-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      // Preserve the card's box type in case it was styled by tag selector.
      clone.style.display = getComputedStyle(original).display;
      return clone;
    }

    build() {
      this.originals = Array.from(this.carousel.children)
        .filter((el) => !el.hasAttribute('data-wd-loop-clone'));

      if (this.originals.length < 2) return;

      this.setWidth = this.measureSetWidth();
      if (this.setWidth <= 0) { requestAnimationFrame(() => this.build()); return; }

      const copies = Math.max(1, Math.ceil((this.carousel.clientWidth * 1.5) / this.setWidth));
      this.leadWidth = this.setWidth * copies;

      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      for (let i = 0; i < copies; i++) {
        this.originals.forEach((c) => before.appendChild(this.makeClone(c)));
        this.originals.forEach((c) => after.appendChild(this.makeClone(c)));
      }

      try {
        this.carousel.prepend(before);
        this.carousel.append(after);
      } catch (err) {
        // Safety net: never leave half-inserted clones behind.
        this.carousel.querySelectorAll('[data-wd-loop-clone]').forEach((n) => n.remove());
        console.warn('[wd-loop] clone insertion failed, loop disabled', err);
        return;
      }

      this.jumpTo(this.leadWidth);
      this.ready = true;

      this.carousel.addEventListener('scroll', () => this.normalize(), { passive: true });
      this.carousel.addEventListener('pointerdown', () => { this.dragging = true; });
      const endDrag = () => {
        if (!this.dragging) return;
        this.dragging = false;
        requestAnimationFrame(() => this.normalize());
      };
      document.addEventListener('pointerup', endDrag);
      document.addEventListener('pointercancel', endDrag);
      window.addEventListener('resize', () => this.onResize());

      this.setupControls();
    }

    jumpTo(px) {
      this.locked = true;
      this.carousel.scrollLeft = isRTL() ? -px : px;
      requestAnimationFrame(() => { this.locked = false; });
    }

    normalize() {
      if (!this.ready || this.locked || this.dragging) return;
      const abs = Math.abs(this.carousel.scrollLeft);
      const offset = abs - this.leadWidth;
      if (offset < 0 || offset >= this.setWidth) {
        const wrapped = ((offset % this.setWidth) + this.setWidth) % this.setWidth;
        this.carousel.scrollLeft = (isRTL() ? -1 : 1) * (this.leadWidth + wrapped);
      }
    }

    controlButtons() {
      const id = this.carousel.id;
      if (!id) return [];
      return Array.from(document.querySelectorAll(
        `[is="carousel-prev-button"][aria-controls="${id}"], [is="carousel-next-button"][aria-controls="${id}"]`
      ));
    }

    setupControls() {
      this.controlButtons().forEach((btn) => {
        btn.disabled = false;

        // A loop has no edges — stop the theme from ever disabling the button.
        const keepEnabled = new MutationObserver(() => { if (btn.disabled) btn.disabled = false; });
        keepEnabled.observe(btn, { attributes: true, attributeFilter: ['disabled'] });

        // Replace the theme's clamped click with a wrapping smooth scroll.
        const dir = btn.matches('[is="carousel-prev-button"]') ? -1 : 1;
        btn.addEventListener('click', (event) => {
          event.stopImmediatePropagation();
          event.preventDefault();
          this.step(dir);
        }, { capture: true });
      });
    }

    step(dir) {
      const cell = this.originals[0].offsetWidth + this.gap();
      const perView = Math.max(1, Math.floor(this.carousel.clientWidth / cell));
      this.carousel.scrollBy({ left: dir * cell * perView * (isRTL() ? -1 : 1), behavior: 'smooth' });
    }

    onResize() {
      if (!this.ready) return;
      this.setWidth = this.measureSetWidth();
      requestAnimationFrame(() => this.normalize());
    }
  }

  const boot = (root = document) => {
    root.querySelectorAll('scroll-carousel[wd-loop]').forEach((carousel) => {
      if (carousel.__wdLoop) return;
      carousel.__wdLoop = new WdInfiniteLoop(carousel);
    });
  };

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('scroll-carousel').then(() => boot());
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
